import type { GrowthSettingsSavePayload, StBrandToneId, StTabId } from '@elva/ui';
import { GrowthDashboardTopBarActions, GrowthSettingsView } from '@elva/ui';
import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import {
  buildAutopilotToggles,
  buildNotificationToggles,
  normalizeOauthService,
} from '@/features/settings/api';
import { useGrowthSettings, useGrowthSettingsMutations } from '@/features/settings/hooks/useGrowthSettings';
import {
  parseSettingsSection,
  SETTINGS_DEFAULT_SECTION,
  settingsPath,
} from '@/features/settings/routes';

const EMPTY_EXCLUDED_POSTAL_CODES: string[] = [];

export function GrowthSettingsPage() {
  const { section: sectionParam } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const legacyTab = searchParams.get('tab');

  const routeTab = useMemo(() => {
    const fromParams = parseSettingsSection(sectionParam);
    if (fromParams) return fromParams;
    const sectionFromPath = location.pathname.split('/')[2];
    return parseSettingsSection(sectionFromPath);
  }, [location.pathname, sectionParam]);

  useEffect(() => {
    if (!legacyTab) return;
    const parsed = parseSettingsSection(legacyTab);
    if (parsed) {
      navigate(settingsPath(parsed), { replace: true });
    }
  }, [legacyTab, navigate]);

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useGrowthSettings();
  const { save, startOauth, disconnect } = useGrowthSettingsMutations();

  const settings = data?.settings;
  const autopilotToggles = useMemo(
    () => buildAutopilotToggles(settings?.autopilotRules ?? {}),
    [settings?.autopilotRules],
  );
  const notificationToggles = useMemo(
    () => buildNotificationToggles(settings?.notifications ?? {}),
    [settings?.notifications],
  );
  const excludedPostalCodes = useMemo(
    () => settings?.excludedPostalCodes ?? EMPTY_EXCLUDED_POSTAL_CODES,
    [settings?.excludedPostalCodes],
  );

  const loading = isFetching || save.isPending || startOauth.isPending || disconnect.isPending;

  const handleSave = useCallback(
    (payload: Partial<GrowthSettingsSavePayload>) => {
      if (!data) return;
      save.mutate({ payload, rawPractice: data.rawPractice });
    },
    [data, save],
  );

  const handleConnect = useCallback(
    (provider: string) => {
      const service = normalizeOauthService(provider);
      if (!service) return;
      startOauth.mutate(service);
    },
    [startOauth],
  );

  const handleDisconnect = useCallback(
    (provider: string) => {
      disconnect.mutate(provider);
    },
    [disconnect],
  );

  const handleTabChange = useCallback(
    (tab: StTabId) => {
      navigate(settingsPath(tab));
    },
    [navigate],
  );

  if (legacyTab && parseSettingsSection(legacyTab)) {
    return null;
  }

  if (!routeTab) {
    return <Navigate to={settingsPath(SETTINGS_DEFAULT_SECTION)} replace />;
  }

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
    >
      <GrowthSettingsView
        key={routeTab}
        {...viewProps({
          activeTab: routeTab,
          initialBudgetValues: settings?.budget,
          initialProfileValues: settings?.profile,
          doctors: settings?.doctors,
          connections: settings?.connections,
          autopilotToggles,
          notificationToggles,
          targetRadiusMiles: settings?.targetRadiusMiles,
          competitorScanRadiusKm: settings?.competitorScanRadiusKm,
          excludedPostalCodes,
          initialBrandTone: (settings?.brandTone as StBrandToneId | undefined) ?? 'friendly-warm',
          spanishContent: settings?.spanishContent,
          loading,
          onSave: handleSave,
          onConnect: handleConnect,
          onDisconnect: handleDisconnect,
          onTabChange: handleTabChange,
        })}
      />
    </AsyncBoundary>
  );
}
